// src/pages/transporteur/Dashboard.js - COMPLETE UPDATED VERSION
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Map from '../../components/map/Map';
import useWebSocket from '../../hooks/useWebSocket';
import { getChauffeursPositions } from '../../services/positionService';
import { calculateMissionEta } from '../../hooks/useEtaCalculator';
import api from '../../services/api';
import { Doughnut, Bar } from 'react-chartjs-2';
import { CHART_COLORS } from './reports/utils/chartConfig';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import '../../styles/tables.css';
import { useNavigate } from "react-router-dom";
import { useI18n } from '../../i18n/I18nContext';
import { useUser } from '../../context/UserContext';
import './Dashboard.css';
import './MiniTimeline.css';
import { useFilter } from '../../context/FilterContext';
import transporteurService from '../../services/transporteurService';


ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, ChartDataLabels);

// 🔥 FIX: Create a memoized Map component wrapper
const MemoizedMap = React.memo(({ chauffeurs, center, zoom, enableAutoZoom, enableClustering, onMarkerClick, style, options }) => {
  const mapRef = useRef(null);

  return (
    <Map
      ref={mapRef}
      chauffeurs={chauffeurs}
      center={center}
      zoom={zoom}
      enableAutoZoom={enableAutoZoom}
      enableClustering={enableClustering}
      onMarkerClick={onMarkerClick}
      style={style}
      options={options}
    />
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders

  // Compare chauffeurs arrays
  if (prevProps.chauffeurs !== nextProps.chauffeurs) {
    if (prevProps.chauffeurs.length !== nextProps.chauffeurs.length) return false;

    for (let i = 0; i < prevProps.chauffeurs.length; i++) {
      const prev = prevProps.chauffeurs[i];
      const next = nextProps.chauffeurs[i];

      if (!prev || !next) return false;
      if (prev.id !== next.id) return false;
      if (prev.latitude !== next.latitude) return false;
      if (prev.longitude !== next.longitude) return false;
      if (prev.isSubcontracted !== next.isSubcontracted) return false;
      if (prev.chauffeurName !== next.chauffeurName) return false;
    }
  }

  // Compare other props
  const propsToCompare = [
    'center', 'zoom', 'enableAutoZoom', 'enableClustering',
    'onMarkerClick', 'style', 'options'
  ];

  for (const prop of propsToCompare) {
    if (prevProps[prop] !== nextProps[prop]) {
      return false;
    }
  }

  return true;
});

const TransporteurDashboard = () => {
  const [stats, setStats] = useState({
    totalChauffeurs: 0,
    activeMissions: 0,
    completedMissions: 0,
    availableChauffeurs: 0
  });

  const [recentMissions, setRecentMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [chauffeursPositions, setChauffeursPositions] = useState([]);
  const [loadingPositions, setLoadingPositions] = useState(true);
  const [errorPositions, setErrorPositions] = useState(null);

  const navigate = useNavigate();
  const { t } = useI18n();
  const { userId, token } = useUser();
  const { selectedTransporteurId } = useFilter();

  // Use ref for t to stabilize fetchPositions dependency
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  // 🔥 FIX: Memoize all static props for Map component
  const mapCenter = useMemo(() => [9.5375, 33.8869], []);
  const mapZoom = 6;
  const mapEnableAutoZoom = false;
  const mapEnableClustering = true;

  const mapStyle = useMemo(() => ({
    height: '100%',
    minHeight: '31.25rem', /* 500px / 16 = 31.25rem */
    borderRadius: '12px',
    overflow: 'hidden'
  }), []);

  const mapOptions = useMemo(() => ({
    style: 'mapbox://styles/mapbox/streets-v11',
    maxZoom: 18,
    minZoom: 4
  }), []);

  // 🔥 FIX: Memoize event handlers
  const handleMarkerClick = useCallback((chauffeur) => {
    // console.log('Selected chauffeur:', chauffeur);
    // You can add additional logic here, like showing a popup or navigating to details
  }, []);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    try {
      const [statsData, missionsData] = await Promise.all([
        transporteurService.getDashboardStats(selectedTransporteurId),
        transporteurService.getRecentMissions(selectedTransporteurId)
      ]);

      setStats(statsData);
      const missions = Array.isArray(missionsData)
        ? missionsData.slice(0, 10)
        : [];
      setRecentMissions(missions);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedTransporteurId]);

  // Fetch positions helper
  const updateDriversList = useCallback((data) => {
    // Transform and validate the data
    const validPositions = (Array.isArray(data) ? data : [])
      .filter(driver =>
        driver &&
        ((typeof driver.latitude === 'number' || !isNaN(parseFloat(driver.latitude))) &&
          (typeof driver.longitude === 'number' || !isNaN(parseFloat(driver.longitude)))) &&
        (driver.id || driver.chauffeurId)
      )
      .map(driver => {
        const normalizedId = driver.chauffeurId || driver.id;
        return {
          ...driver, // Spread first — includes missionStatus, arrivalLat/Lng etc from backend
          id: normalizedId,
          chauffeurId: normalizedId,
          chauffeurName: driver.chauffeurName || driver.name || `Driver ${normalizedId}`,
          latitude: typeof driver.latitude === 'number' ? driver.latitude : parseFloat(driver.latitude),
          longitude: typeof driver.longitude === 'number' ? driver.longitude : parseFloat(driver.longitude),
          available: driver.available || false,
          isSubcontracted: driver.isSubcontracted || driver.subcontracted || false,
        };
      });

    if (validPositions.length > 0) {
      console.log(`[Dashboard] Sync: ${validPositions.length} drivers. IDs:`, validPositions.map(p => p.id));
    }

    // Only update state if positions actually changed
    // Round latitude/longitude to 5 decimals to avoid floating-point noise
    const roundPosition = pos => ({
      ...pos,
      latitude: Math.round(pos.latitude * 1e5) / 1e5,
      longitude: Math.round(pos.longitude * 1e5) / 1e5,
    });
    const roundedNewPositions = validPositions.map(roundPosition);
    setChauffeursPositions(prev => {
      if (prev.length !== roundedNewPositions.length) {
        return roundedNewPositions;
      }

      // Deep compare (rounded)
      const positionsChanged = prev.some((prevPos, index) => {
        const newPos = roundedNewPositions[index];
        if (!newPos) return true;
        return (
          prevPos.id !== newPos.id ||
          prevPos.isSubcontracted !== newPos.isSubcontracted ||
          (Math.abs(prevPos.latitude - newPos.latitude) > 1e-5) ||
          (Math.abs(prevPos.longitude - newPos.longitude) > 1e-5)
        );
      });

      if (positionsChanged) {
        return roundedNewPositions;
      }

      return prev;
    });

    // Async fetch ETAs for popup — use coordinates from the position object directly
    // (backend now returns departureLat/Lng, arrivalLat/Lng, missionStatus per driver position)
    roundedNewPositions.forEach(async (driver) => {
      if (driver.arrivalLat && driver.arrivalLng) {
        try {
          const etaData = await calculateMissionEta({
            driverLat: driver.latitude,
            driverLng: driver.longitude,
            pickupLat: driver.departureLat,
            pickupLng: driver.departureLng,
            destLat: driver.arrivalLat,
            destLng: driver.arrivalLng,
            missionStarted: driver.missionStatus === 'EN_COURS' && driver.hasDepartedLoading,
            driverSpeed: driver.speed,
            vehicleType: driver.vehicleType || 'HEAVY'
          });

          setChauffeursPositions(prev => prev.map(p =>
            p.id === driver.id
              ? { ...p, remainingMinutes: etaData.remainingMinutes, estimatedArrival: etaData.estimatedArrival, distanceKm: etaData.distanceKm }
              : p
          ));
        } catch (e) {
          // ignore if route unavailable for this driver
        }
      }
    });

    // Log info only on initial load or significant changes
    if (validPositions.length > 0 && Math.random() < 0.05) {
      console.log(`[SaaS][Dashboard] Data sync: ${validPositions.length} drivers found.`);
    }

  }, []);

  const fetchPositions = useCallback(async (isBackground = false) => {
    try {
      if (!isBackground) {
        setLoadingPositions(true);
      }
      const data = await getChauffeursPositions(selectedTransporteurId);
      updateDriversList(data);
      setErrorPositions(null);
    } catch (err) {
      setErrorPositions(tRef.current('common.errorLoadingPositions') || 'Error loading positions');
    } finally {
      if (!isBackground) {
        setLoadingPositions(false);
      }
    }
  }, [updateDriversList, selectedTransporteurId]);

  // Initial data fetch
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData, userId]);

  // Real-time WebSocket connection
  const handleWebSocketMessage = useCallback((newPos) => {
    if (newPos && (newPos.id || newPos.chauffeurId)) {
      setChauffeursPositions(prev => {
        // Fix: Normalize ID consistent with polling (prefer chauffeurId)
        const updateId = newPos.chauffeurId || newPos.id;

        // Fix: Use loose equality / String comparison for safety across numeric/string types
        const index = prev.findIndex(p => String(p.id) === String(updateId));

        const roundedPos = {
          ...newPos,
          id: updateId, // Ensure state uses the stable ID
          chauffeurId: updateId,
          latitude: Math.round(parseFloat(newPos.latitude) * 1e5) / 1e5,
          longitude: Math.round(parseFloat(newPos.longitude) * 1e5) / 1e5,
        };

        if (index === -1) return [...prev, roundedPos];

        const newPositions = [...prev];
        // Merge updates safely
        newPositions[index] = { ...prev[index], ...roundedPos };
        return newPositions;
      });
    }
  }, []);

  const { isConnected } = useWebSocket({
    url: process.env.REACT_APP_WS_URL,
    topic: '/topic/positions',
    onMessage: handleWebSocketMessage,
    accessToken: token,
    debug: true
  });

  // Setup positions polling (Keep as backup, but slower)
  useEffect(() => {
    fetchPositions(false);

    // Fallback polling: If connected via WS, poll rarely (5 min). If disconnected, poll often (15s).
    const intervalTime = isConnected ? 300000 : 15000;
    const pollingInterval = setInterval(() => fetchPositions(true), intervalTime);

    return () => {
      clearInterval(pollingInterval);
    };
  }, [fetchPositions, isConnected]);



  // Memoize chart data
  const availabilityData = useMemo(() => ({
    labels: [t('dashboard.stats.available'), t('common.busy')],
    datasets: [
      {
        data: [stats.availableChauffeurs, stats.totalChauffeurs - stats.availableChauffeurs],
        backgroundColor: [CHART_COLORS.success, CHART_COLORS.danger],
        borderWidth: 0,
        hoverOffset: 4
      },
    ],
  }), [stats.availableChauffeurs, stats.totalChauffeurs, t]);

  const availabilityOptions = useMemo(() => ({
    plugins: {
      datalabels: {
        color: '#fff',
        font: { weight: 'bold', size: 14 },
        formatter: (value) => value > 0 ? value : '',
      },
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true,
          font: { size: 12, weight: '500' }
        }
      },
    },
    cutout: '75%',
    maintainAspectRatio: false,
  }), []);

  const formatDate = (value) => {
    if (!value) return '';
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
  };

  const enAttenteCount = useMemo(() =>
    typeof stats.pendingMissions !== 'undefined'
      ? stats.pendingMissions
      : recentMissions.filter(m => m.status === 'EN_ATTENTE').length,
    [stats.pendingMissions, recentMissions]
  );

  const cancelledCount = useMemo(() =>
    typeof stats.cancelledMissions !== 'undefined'
      ? stats.cancelledMissions
      : recentMissions.filter(m => m.status === 'ANNULEE').length,
    [stats.cancelledMissions, recentMissions]
  );

  const missionStateData = useMemo(() => ({
    labels: [
      t('common.status.pending'),
      t('common.status.active'),
      t('common.status.completed'),
      t('common.status.cancelled')
    ],
    datasets: [
      {
        label: t('common.missions'),
        data: [
          enAttenteCount,
          stats.activeMissions || 0,
          stats.completedMissions || 0,
          cancelledCount
        ],
        backgroundColor: [
          CHART_COLORS.pending,
          CHART_COLORS.active,
          CHART_COLORS.completed,
          CHART_COLORS.cancelled
        ],
        borderRadius: 8,
        maxBarThickness: 45,
        borderSkipped: false,
      },
    ],
  }), [enAttenteCount, stats.activeMissions, stats.completedMissions, cancelledCount, t]);

  const missionStateOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: false },
      datalabels: {
        color: (context) => {
          // Adjust label color based on bar color brightness
          const bg = context.dataset.backgroundColor[context.dataIndex];
          return bg === CHART_COLORS.pending ? '#475569' : '#fff';
        },
        font: { weight: '600', size: 12 },
        anchor: 'end',
        align: 'top',
        offset: 0,
        formatter: (value) => value > 0 ? value : '',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        precision: 0,
        grid: { display: false },
        border: { display: false },
        ticks: { display: false }
      },
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: {
          color: '#64748b',
          font: { size: 12, weight: '500' }
        }
      }
    }
  }), []);

  const renderMiniTimeline = (mission) => {
    const steps = [
      { label: t('missions.status.timeline.departure') || 'Départ', done: true },
      { label: t('missions.status.timeline.arrivalLoading') || 'Arrivée chargement', done: !!mission.loadingArrivalTime },
      { label: t('missions.status.timeline.departureLoading') || 'Départ chargement', done: !!mission.loadingDepartureTime },
      { label: t('missions.status.timeline.arrivalDelivery') || 'Arrivée livraison', done: !!mission.deliveryArrivalTime },
      { label: t('missions.status.timeline.completed') || 'Terminée', done: mission.status === 'TERMINEE' || mission.status === 'ANNULEE' }
    ];

    return (
      <div className="mini-timeline">
        {steps.map((step, idx) => {
          const isNext = !step.done && (idx === 0 || steps[idx - 1].done);
          return (
            <div
              key={idx}
              className={`mini-step ${step.done ? 'done' : ''} ${isNext ? 'active' : ''}`}
              data-label={step.label}
            >
              <div className="mini-dot"></div>
              {idx < steps.length - 1 && <div className="mini-line"></div>}
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '25rem' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="alert alert-danger rounded-3 border-0 shadow-sm">{error}</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="modern-dashboard" style={{ background: '#f8fafc', minHeight: '100vh' }}>
        {/* Header */}
        <div className="dashboard-header mb-5">
          <h1 className="display-6 fw-bold text-dark mb-2">{t('dashboard.pageTitle')}</h1>
          <p className="text-muted fs-5 mb-0">{t('dashboard.pageSubtitle')}</p>
        </div>

        {/* Stats Cards */}
        <div className="row g-4 mb-5">
          <div className="col-xl-3 col-md-6">
            <div className="stats-card" style={{
              background: 'linear-gradient(135deg, #1E3A8A 0%, #1D4ED8 100%)',
              padding: '2rem',
              borderRadius: '1.25rem',
              color: 'white',
              boxShadow: '0 0.625rem 1.875rem rgba(0, 0, 0, 0.15)',
              border: 'none',
              minHeight: '8.75rem'
            }}>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h3 className="fw-bold mb-1">{stats.totalChauffeurs}</h3>
                  <p className="mb-0 opacity-75">{t('dashboard.stats.totalDrivers')}</p>
                </div>
                <div className="stats-icon" style={{
                  width: '3rem',
                  height: '3rem',
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <i className="bi bi-people fs-4"></i>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="stats-card" style={{
              background: 'linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)',
              padding: '2rem',
              borderRadius: '1.25rem',
              color: 'white',
              boxShadow: '0 0.625rem 1.875rem rgba(0, 0, 0, 0.15)',
              border: 'none',
              minHeight: '8.75rem'
            }}>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h3 className="fw-bold mb-1">{stats.activeMissions}</h3>
                  <p className="mb-0 opacity-75">{t('dashboard.stats.activeMissions')}</p>
                </div>
                <div className="stats-icon" style={{
                  width: '48px',
                  height: '48px',
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <i className="bi bi-truck fs-4"></i>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="stats-card" style={{
              background: 'linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%)',
              padding: '2rem',
              borderRadius: '1.25rem',
              color: 'white',
              boxShadow: '0 0.625rem 1.875rem rgba(0, 0, 0, 0.15)',
              border: 'none',
              minHeight: '8.75rem'
            }}>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h3 className="fw-bold mb-1">{stats.completedMissions}</h3>
                  <p className="mb-0 opacity-75">{t('dashboard.stats.completedMissions')}</p>
                </div>
                <div className="stats-icon" style={{
                  width: '48px',
                  height: '48px',
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <i className="bi bi-check-circle fs-4"></i>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="stats-card" style={{
              background: 'linear-gradient(135deg, #3B82F6 0%, #4F8EF7 100%)',
              padding: '2rem',
              borderRadius: '1.25rem',
              color: 'white',
              boxShadow: '0 0.625rem 1.875rem rgba(0, 0, 0, 0.15)',
              border: 'none',
              minHeight: '8.75rem'
            }}>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h3 className="fw-bold mb-1">{stats.availableChauffeurs}</h3>
                  <p className="mb-0 opacity-75">{t('dashboard.stats.available')}</p>
                </div>
                <div className="stats-icon" style={{
                  width: '48px',
                  height: '48px',
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <i className="bi bi-person-check fs-4"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="row g-4 mb-5">
          {/* Map - Left Side */}
          <div className="col-xl-8 col-lg-7">
            <div className="modern-card" style={{
              background: '#fff',
              borderRadius: '24px',
              boxShadow: '0 4px 25px rgba(0,0,0,0.08)',
              border: 'none',
              height: '36.875rem', /* 590px / 16 = 36.875rem */
              overflow: 'hidden'
            }}>
              <div className="card-header" style={{
                background: '#fff',
                border: 'none',
                padding: '2rem 2rem 1rem'
              }}>
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="fw-bold text-dark mb-0">{t('dashboard.maps.driverLocations')}</h5>
                  <div className="d-flex align-items-center text-muted">
                    <div className="me-3 px-2 py-1 rounded bg-light border d-flex gap-3 small">
                      <span className="text-primary"><i className="bi bi-circle-fill me-1"></i> {chauffeursPositions.filter(p => !p.isSubcontracted).length} {t('dashboard.maps.own')}</span>
                      <span style={{ color: '#F59E0B' }}><i className="bi bi-circle-fill me-1"></i> {chauffeursPositions.filter(p => p.isSubcontracted).length} {t('dashboard.maps.subcontracted')}</span>
                    </div>
                    <i className="bi bi-geo-alt me-2"></i>
                    <span className="small">{t('dashboard.maps.realTimeTracking')}</span>
                    {isConnected ? (
                      <span className="badge bg-success ms-2" style={{ fontSize: '0.7rem' }}>LIVE</span>
                    ) : (
                      <span className="badge bg-secondary ms-2" style={{ fontSize: '0.7rem' }}>OFFLINE</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="card-body p-0">
                {errorPositions && (
                  <div className="alert alert-danger m-3 rounded-3 border-0" role="alert">
                    {errorPositions}
                  </div>
                )}
                <div className="dashboard-map-container" style={{ height: '31.25rem', width: '100%', borderRadius: '0.75rem', overflow: 'hidden' }}>
                  {loadingPositions ? (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      background: '#f8f9fa',
                      borderRadius: '12px'
                    }}>
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : errorPositions ? (
                    <div className="alert alert-danger m-3">
                      {errorPositions}
                    </div>
                  ) : (
                    <MemoizedMap
                      chauffeurs={chauffeursPositions}
                      center={{ latitude: 35.0, longitude: -4.0 }} // Fallback: Morocco/Algeria/Spain
                      zoom={5}
                      enableAutoZoom={false} // Disable auto-zoom for low billing
                      enableClustering={mapEnableClustering}
                      onMarkerClick={handleMarkerClick}
                      style={mapStyle}
                      options={mapOptions}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Charts - Right Side */}
          <div className="col-xl-4 col-lg-5">
            <div className="row g-4 h-100">
              {/* Driver Availability Chart */}
              <div className="col-12">
                <div className="modern-card h-100" style={{
                  background: '#fff',
                  borderRadius: '24px',
                  boxShadow: '0 4px 25px rgba(0,0,0,0.08)',
                  border: 'none'
                }}>
                  <div className="card-body p-4">
                    <h6 className="fw-bold text-dark mb-3">{t('dashboard.charts.driverAvailability')}</h6>
                    <div style={{ height: '12.5rem' }}>
                      <Doughnut data={availabilityData} options={availabilityOptions} plugins={[ChartDataLabels]} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Mission States Chart */}
              <div className="col-12">
                <div className="modern-card h-100" style={{
                  background: '#fff',
                  borderRadius: '24px',
                  boxShadow: '0 4px 25px rgba(0,0,0,0.08)',
                  border: 'none'
                }}>
                  <div className="card-body p-4">
                    <h6 className="fw-bold text-dark mb-3">{t('dashboard.charts.missionOverview')}</h6>
                    <div style={{ height: '12.5rem' }}>
                      <Bar data={missionStateData} options={missionStateOptions} plugins={[ChartDataLabels]} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Missions Table */}
        <div className="row">
          <div className="col-12">
            <div className="modern-card" style={{
              background: '#fff',
              borderRadius: '24px',
              boxShadow: '0 4px 25px rgba(0,0,0,0.08)',
              border: 'none'
            }}>
              <div className="card-header" style={{
                background: '#fff',
                border: 'none',
                padding: '2rem 2rem 1rem',
                borderRadius: '24px 24px 0 0'
              }}>
                <h5 className="fw-bold text-dark mb-0">{t('dashboard.recentMissions.title')}</h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>{t('missions.missionList.mission')}</th>
                        <th>{t('dashboard.recentMissions.status')}</th>
                        <th>Progression</th>
                        <th>{t('missions.missionList.date')}</th>
                        <th>{t('dashboard.recentMissions.driver')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentMissions.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center text-muted py-5">
                            <i className="bi bi-inbox fs-1 mb-3 d-block"></i>
                            {t('dashboard.recentMissions.noMissions')}
                          </td>
                        </tr>
                      ) : (
                        recentMissions.map(mission => (
                          <tr key={mission.id}
                            className="cursor-pointer"
                            onClick={() => navigate(`/missions/edit/${mission.id}`)}>
                            <td>
                              {mission.departureLocation && mission.arrivalLocation ? (
                                <div className="d-flex align-items-center gap-2">
                                  <span className="d-flex align-items-center text-dark">
                                    <i className="bi bi-geo-alt-fill me-1 text-primary"></i>
                                    {mission.departureLocation}
                                  </span>
                                  <i className="bi bi-arrow-right text-muted"></i>
                                  <span className="d-flex align-items-center text-dark">
                                    <i className="bi bi-flag-fill me-1 text-success"></i>
                                    {mission.arrivalLocation}
                                  </span>
                                </div>
                              ) : (
                                `#${mission.id}`
                              )}
                            </td>

                            <td>
                              <span className={`badge rounded-pill ${mission.status === 'EN_ATTENTE' ? 'bg-warning text-dark' :
                                mission.status === 'EN_COURS' ? 'bg-primary' :
                                  mission.status === 'TERMINEE' ? 'bg-success' :
                                    mission.status === 'ANNULEE' ? 'bg-danger' : 'bg-secondary'
                                }`}>
                                {t(`missions.status.${(mission.status || '').toLowerCase()}`)}
                              </span>
                            </td>
                            <td>{renderMiniTimeline(mission)}</td>
                            <td>{formatDate(mission.departureDate || mission.date)}</td>
                            <td>
                              {mission.chauffeur ? (
                                <div className="d-flex align-items-center">
                                  <div className="avatar-sm me-2" style={{
                                    width: '2rem',
                                    height: '2rem',
                                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: '0.75rem',
                                    fontWeight: 'bold'
                                  }}>
                                    {mission.chauffeur.charAt(0)}
                                  </div>
                                  <span>{mission.chauffeur}</span>
                                </div>
                              ) : (
                                <span className="text-muted fst-italic">{t('dashboard.recentMissions.notAssigned')}</span>
                              )}
                            </td>

                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default React.memo(TransporteurDashboard);